import { Card } from 'react-bootstrap'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    DragDropContext,
    Draggable,
    DraggableProvided,
    DraggableStateSnapshot,
    Droppable,
    DroppableProvided,
} from '@hello-pangea/dnd'
import { TableRowAdvanced } from './TableRowAdvanced'
import { generateElementID } from '../../../../../utils/generateElementID'
import { TableAdvancedHeader } from './TableAdvancedHeader'
import { TableAdvancedFooter } from './TableAdvancedFooter'
import { TableVirtuoso } from 'react-virtuoso'
import { TableHeadAdvanced } from './TableHeadAdvanced'
import _isEqual from 'lodash/isEqual'

interface TableAdvancedProps {
    label: string
    config: any
    schema: any
    path: string[]
    itemEditIndex: number | undefined
    scalarsName?: string
    isRuleCard?: boolean
}

export const TableAdvanced = (props: TableAdvancedProps) => {
    const { label, config, schema, path, itemEditIndex, scalarsName, isRuleCard } = props

    const [focusIndex, setFocusIndex] = useState(itemEditIndex ?? -1)
    const [isEditMode, setEditMode] = useState(
        typeof itemEditIndex !== 'undefined'
    )
    const rowsEditingMap = useMemo(
        () =>
            (config ?? []).reduce((acc: Record<string, boolean>, cur: any) => {
                acc[cur.element_id] = false
                return acc
            }, {}),
        [config]
    ) as Record<string, boolean>

    const [rowsEditing, setRowsEditing] = useState(rowsEditingMap)
    const [stateDerivedFromConfig, setStateDerivedFromConfig] = useState(
        config ?? []
    )
    const configStateRef = useRef(config)
    const virtuosoRef = useRef<any>(null)
    const invalidRowsRef = useRef<Record<string, boolean>>({})

    const componentSchema = schema?.children?.children || schema?.children
    const { is_order_user_defined: isOrderUserDefined } = schema
    const toolTipName = componentSchema.name ? componentSchema.name : 'row'

    const handleValidity = useCallback(
        (elementId: string, isValid: boolean) => {
            if (!isValid) {
                invalidRowsRef.current[elementId] = true
            }
            if (isValid && invalidRowsRef.current[elementId]) {
                delete invalidRowsRef.current[elementId]
            }
        },
        []
    )

    const handleRowsEditingChange = useCallback(
        (elementId: string, isEditing: boolean) => {
            setStateDerivedFromConfig(() => configStateRef.current)
            setRowsEditing((prev) => ({ ...prev, [elementId]: isEditing }))
        },
        []
    )

    const focusItem = useCallback((index: number) => {
        setFocusIndex(index)
    }, [])

    const scrollIntoRow = useCallback((index: number) => {
        setTimeout(() => {
            virtuosoRef.current?.scrollToIndex({
                index: index,
                align: 'center',
                behavior: 'smooth',
            })
        }, 150) // !important to hold right timeout
    }, [])

    const handleDnd = (result: any) => {
        if (!result.destination) return
        setStateDerivedFromConfig(() => configStateRef.current)
        setStateDerivedFromConfig((prev: typeof stateDerivedFromConfig) => {
            const items = [...prev]
            const [reorderedItem] = items.splice(result.source.index, 1)
            items.splice(result.destination.index, 0, reorderedItem)
            configStateRef.current = items
            return items
        })
        focusItem(-1)
        scrollIntoRow(result.destination.index)
    }

    const moveTableRow = useCallback((toPosition: number, index: number) => {
        setStateDerivedFromConfig(() => configStateRef.current)
        setStateDerivedFromConfig((prev: typeof stateDerivedFromConfig) => {
            const items = [...prev]
            const [reorderedItem] = items.splice(Number(index), 1)
            const moveTo = Number(toPosition) - 1
            items.splice(moveTo, 0, reorderedItem)
            configStateRef.current = items
            return items
        })
        focusItem(-1)
        scrollIntoRow(toPosition)
    }, [])

    const modifyTableRow = useCallback(
        (name: string, value: any, index: number) => {
            const dataItems = configStateRef.current
            const result = dataItems.map((el: any, i: number) =>
                i === index ? { ...el, [name]: value } : el
            )
            configStateRef.current = result
        },
        []
    )

    const deleteTableRow = useCallback((index: number) => {
        setStateDerivedFromConfig(() => configStateRef.current)
        setStateDerivedFromConfig((prev: any[]) => {
            const result = prev.filter((_, i) => i !== index)
            configStateRef.current = result
            return result
        })
    }, [])

    const duplicateTableRow = useCallback((position: number) => {
        setStateDerivedFromConfig(() => configStateRef.current)
        setStateDerivedFromConfig((prev: typeof stateDerivedFromConfig) => {
            const itemIndex = position - 1
            const item = prev[itemIndex]
            const result = prev.toSpliced(position, 0, {
                ...item,
                element_id: generateElementID(),
            })
            configStateRef.current = result
            return result
        })
    }, [])

    const onVirtualTableScroll = (isScrolling: boolean) => {
        if (isScrolling) {
            if (_isEqual(stateDerivedFromConfig, configStateRef.current)) return
            setStateDerivedFromConfig(() => configStateRef.current)
        }
    }

    useEffect(() => {
        if (itemEditIndex) scrollIntoRow(itemEditIndex)
    }, [itemEditIndex])

    return (
        <Card>
            <TableAdvancedHeader title={label} />
            <Card.Body className="p-0">
                <div className="w-100">
                    <DragDropContext onDragEnd={handleDnd}>
                        <Droppable
                            direction="vertical"
                            droppableId="app-table"
                        >
                            {(provided: DroppableProvided) => (
                                <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                >
                                    <TableVirtuoso
                                        ref={virtuosoRef}
                                        style={{
                                            height: 'calc(100vh - 170px)',
                                        }}
                                        overscan={{
                                            main: 10,
                                            reverse: 10,
                                        }}
                                        context={{
                                            data: stateDerivedFromConfig,
                                        }}
                                        isScrolling={onVirtualTableScroll}
                                        components={{
                                            Table: (props) => (
                                                <table
                                                    className="m-0 table app-table table-hover p-relative"
                                                    {...props}
                                                />
                                            ),
                                            TableRow: (props) => {
                                                const {
                                                    context,
                                                    ...otherProps
                                                } = props
                                                const data = context?.data
                                                const idx = props['data-index']
                                                const item = data?.[idx]
                                                const elementId =
                                                    item?.element_id

                                                return (
                                                    <Draggable
                                                        isDragDisabled={
                                                            !isOrderUserDefined
                                                        }
                                                        key={String(
                                                            elementId
                                                                ? elementId
                                                                : idx
                                                        )}
                                                        draggableId={String(
                                                            elementId
                                                                ? elementId
                                                                : idx
                                                        )}
                                                        index={idx}
                                                    >
                                                        {(
                                                            provided: DraggableProvided,
                                                            snapshot: DraggableStateSnapshot
                                                        ) => (
                                                            <tr
                                                                className="hover"
                                                                {...otherProps}
                                                                {...provided.draggableProps}
                                                                {...provided.dragHandleProps}
                                                                ref={
                                                                    provided.innerRef
                                                                }
                                                                style={
                                                                    snapshot.isDragging
                                                                        ? {
                                                                              display:
                                                                                  'table',
                                                                              background:
                                                                                  '#ececec',
                                                                              tableLayout:
                                                                                  'fixed',
                                                                              ...provided
                                                                                  .draggableProps
                                                                                  .style,
                                                                          }
                                                                        : {
                                                                              display:
                                                                                  'table-row',
                                                                              ...provided
                                                                                  .draggableProps
                                                                                  .style,
                                                                          }
                                                                }
                                                            >
                                                                <TableRowAdvanced
                                                                    name={
                                                                        toolTipName
                                                                    }
                                                                    schema={
                                                                        componentSchema
                                                                    }
                                                                    index={idx}
                                                                    data={item}
                                                                    modifyTableRow={
                                                                        modifyTableRow
                                                                    }
                                                                    deleteTableRow={
                                                                        deleteTableRow
                                                                    }
                                                                    duplicateTableRow={
                                                                        duplicateTableRow
                                                                    }
                                                                    moveTableRow={
                                                                        moveTableRow
                                                                    }
                                                                    onValidityCheck={
                                                                        handleValidity
                                                                    }
                                                                    onItemFocus={
                                                                        focusItem
                                                                    }
                                                                    focusIndex={
                                                                        focusIndex
                                                                    }
                                                                    itemsCount={
                                                                        stateDerivedFromConfig.length
                                                                    }
                                                                    isEditMode={
                                                                        isEditMode
                                                                    }
                                                                    setEditMode={
                                                                        setEditMode
                                                                    }
                                                                    isOrderUserDefined={
                                                                        isOrderUserDefined
                                                                    }
                                                                    onRowEdit={
                                                                        handleRowsEditingChange
                                                                    }
                                                                    isRowEditing={
                                                                        rowsEditing[
                                                                            elementId
                                                                        ]
                                                                    }
                                                                    scalarsName={scalarsName}
                                                                />
                                                            </tr>
                                                        )}
                                                    </Draggable>
                                                )
                                            },
                                        }}
                                        fixedHeaderContent={() => (
                                            <>
                                                <TableHeadAdvanced
                                                    componentSchema={
                                                        componentSchema
                                                    }
                                                    setConfigState={
                                                        setStateDerivedFromConfig
                                                    }
                                                    onScrollIntoRow={
                                                        scrollIntoRow
                                                    }
                                                    configStateRefObj={
                                                        configStateRef
                                                    }
                                                    isEditMode={isEditMode}
                                                    setEditMode={setEditMode}
                                                />
                                            </>
                                        )}
                                        data={stateDerivedFromConfig}
                                    />
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </div>
            </Card.Body>
            <TableAdvancedFooter
                path={path}
                invalidRowsRefObj={invalidRowsRef}
                configStateRefObj={configStateRef}
                scrollIntoRow={scrollIntoRow}
                scalarsName={scalarsName}
                isRuleCard={isRuleCard}
            />
        </Card>
    )
}
